import React, { useState, useEffect, useCallback, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import * as cx from "classnames/bind";
import e from "styles/Carousel.module.scss";

const ToolsCarousel = ({
  tools,
  tools6,
  shadow,
  radius,
  options = {
    align: "start",
    dragFree: true,
    containScroll: "trimSnaps",
    loop: false,
  },
}) => {
  const autoplay = useRef(
    Autoplay(
      { delay: 3000, stopOnInteraction: false },
      (emblaRoot) => emblaRoot.parentElement,
    ),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(options, [autoplay.current]);
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    autoplay.current.reset();
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    autoplay.current.reset();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  const toolsByIndex = [
    { src: "/img/slides/tools/bar_spoon.jpeg", label: "Bar spoon" },
    { src: "/img/slides/tools/bottle_opener.jpeg", label: "Bottle opener" },
    { src: "/img/slides/tools/chopping_board.jpeg", label: "Cutting board" },
    { src: "/img/slides/tools/fine_strainer.jpeg", label: "Fine strainer" },
    { src: "/img/slides/tools/fruit_zester.jpeg", label: "Fruit zester" },
    {
      src: "/img/slides/tools/hawthorne_strainer.jpeg",
      label: "Hawthorne strainer",
    },
    { src: "/img/slides/tools/ice_box.jpeg", label: "Ice bucket" },
    { src: "/img/slides/tools/ice_scoop.jpeg", label: "Ice scoop" },
    { src: "/img/slides/tools/jigger.jpeg", label: "Jigger" },
    { src: "/img/slides/tools/julep_strainer.jpeg", label: "Julep strainer" },
    { src: "/img/slides/tools/knife.jpeg", label: "Knife" },
    {
      src: "/img/slides/tools/mexican_elbow_juicer.jpeg",
      label: "Mexican elbow juicer",
    },
    { src: "/img/slides/tools/mixing_glass.jpeg", label: "Mixing glass" },
    { src: "/img/slides/tools/muddler.jpeg", label: "Muddler" },
    { src: "/img/slides/tools/roll_bag.jpeg", label: "Roll-bag" },
    { src: "/img/slides/tools/shaker.jpeg", label: "Shaker" },
    { src: "/img/slides/tools/speed_pourer.jpeg", label: "Speed pourer" },
  ];

  return (
    <div className={cx(e.emblaHolder)}>
      <div className={cx(e.embla)}>
        <div className={cx(e.embla__viewport)} ref={emblaRef}>
          <div className={cx(e.embla__container)}>
            {toolsByIndex.map((tool, index) => (
              <div
                className={cx(
                  e.embla__slide,
                  e.tool__slide,
                  tools6 ? e.tool_slide_6 : "",
                )}
                key={index}
              >
                <div className={cx(e.embla__slide__inner)}>
                  <Image
                    className={cx(
                      e.embla__slide__img,
                      shadow ? "Shadow" : "",
                      radius ? "Radius ofh" : "",
                    )}
                    src={tool.src}
                    alt={tool.label}
                    layout="responsive"
                    objectFit="cover"
                    width={768}
                    height={960}
                  />
                  <div className={cx(e.embla__slide__text)}>{tool.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
      <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
    </div>
  );
};

export default ToolsCarousel;

export const PrevButton = ({ enabled, onClick }) => (
  <button
    className={cx(e.embla__button, e.Black, e.embla__button__prev)}
    onClick={onClick}
    disabled={!enabled}
  >
    <svg
      className={cx(e.embla__button__svg)}
      viewBox="137.718 -1.001 366.563 644"
    >
      <path d="M428.36 12.5c16.67-16.67 43.76-16.67 60.42 0 16.67 16.67 16.67 43.76 0 60.42L241.7 320c148.25 148.24 230.61 230.6 247.08 247.08 16.67 16.66 16.67 43.75 0 60.42-16.67 16.66-43.76 16.67-60.42 0-27.72-27.71-249.45-249.37-277.16-277.08a42.308 42.308 0 0 1-12.48-30.34c0-11.1 4.1-22.05 12.48-30.42C206.63 234.23 400.64 40.21 428.36 12.5z" />
    </svg>
  </button>
);

export const NextButton = ({ enabled, onClick }) => (
  <button
    className={cx(e.embla__button, e.Black, e.embla__button__next)}
    onClick={onClick}
    disabled={!enabled}
  >
    <svg className={cx(e.embla__button__svg)} viewBox="0 0 238.003 238.003">
      <path d="M181.776 107.719L78.705 4.648c-6.198-6.198-16.273-6.198-22.47 0s-6.198 16.273 0 22.47l91.883 91.883-91.883 91.883c-6.198 6.198-6.198 16.273 0 22.47s16.273 6.198 22.47 0l103.071-103.039a15.741 15.741 0 0 0 4.64-11.283c0-4.13-1.526-8.199-4.64-11.313z" />
    </svg>
  </button>
);
